import {
  DataTexture,
  LinearFilter,
  LinearMipmapLinearFilter,
  NoColorSpace,
  RepeatWrapping,
} from "three";

/** Power-of-two — texture period matches UV wrap so tiles are seamless. */
const SIZE = 512;

function fract(x: number): number {
  return x - Math.floor(x);
}

function wrapIndex(i: number): number {
  return ((i % SIZE) + SIZE) % SIZE;
}

function hash(ix: number, iy: number): number {
  const x = wrapIndex(ix);
  const y = wrapIndex(iy);
  return fract(Math.sin(x * 127.1 + y * 311.7) * 43758.5453);
}

/**
 * Value noise with periodic boundary — texel (0,*) meets (*,SIZE-1) without a seam.
 */
function smoothNoisePeriodic(x: number, y: number): number {
  const xf = ((x % SIZE) + SIZE) % SIZE;
  const yf = ((y % SIZE) + SIZE) % SIZE;
  const x0 = Math.floor(xf);
  const y0 = Math.floor(yf);
  const fx = xf - x0;
  const fy = yf - y0;
  const x1 = wrapIndex(x0 + 1);
  const y1 = wrapIndex(y0 + 1);
  const a = hash(x0, y0);
  const b = hash(x1, y0);
  const c = hash(x0, y1);
  const d = hash(x1, y1);
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  return a * (1 - ux) * (1 - uy) + b * ux * (1 - uy) + c * (1 - ux) * uy + d * ux * uy;
}

/** Toroidal box blur — keeps edges seamless while softening blocky texels / mip crawl. */
function toroidalBlur(src: Uint8Array, dst: Uint8Array, radius: number): void {
  const at = (x: number, y: number) => {
    const xx = wrapIndex(x);
    const yy = wrapIndex(y);
    return (yy * SIZE + xx) * 4;
  };
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      let sum = 0;
      let w = 0;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          sum += src[at(x + dx, y + dy) + 1];
          w += 1;
        }
      }
      const g = Math.floor(sum / w);
      const i = (y * SIZE + x) * 4;
      dst[i] = g;
      dst[i + 1] = g;
      dst[i + 2] = g;
      dst[i + 3] = 255;
    }
  }
}

/**
 * Single low-frequency grain — multi-octave FBM does not tile on a square period,
 * which caused visible repeater seams. Subtle contrast so roughness/bump stay organic.
 */
function createFrameMicroSurfaceTexture(): DataTexture {
  const raw = new Uint8Array(SIZE * SIZE * 4);
  const blurred = new Uint8Array(SIZE * SIZE * 4);
  /* ~6 periods across the texture — fine enough for metal grain, no macro grid. */
  const periods = 6;
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const nx = ((x + 0.5) / SIZE) * periods * SIZE;
      const ny = ((y + 0.5) / SIZE) * periods * SIZE;
      const n = smoothNoisePeriodic(nx, ny);
      /* Tight grey band — modulation only, no harsh grain on reflections */
      const grey = Math.floor((0.47 + n * 0.09) * 255);
      const i = (y * SIZE + x) * 4;
      raw[i] = grey;
      raw[i + 1] = grey;
      raw[i + 2] = grey;
      raw[i + 3] = 255;
    }
  }
  toroidalBlur(raw, blurred, 2);
  toroidalBlur(blurred, raw, 2);

  const tex = new DataTexture(raw, SIZE, SIZE);
  tex.wrapS = tex.wrapT = RepeatWrapping;
  /* Many repeats object-space — individual tiles are tiny; seamless texture hides repeats */
  tex.repeat.set(24, 24);
  tex.generateMipmaps = true;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.magFilter = LinearFilter;
  tex.anisotropy = 8;
  tex.colorSpace = NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

let cached: DataTexture | undefined;
let cacheRevision = -1;
/** Bump when the procedural changes so HMR gets a fresh GPU texture. */
const PROC_REVISION = 2;

export function getFrameMicroSurfaceTexture(): DataTexture {
  if (!cached || cacheRevision !== PROC_REVISION) {
    cached?.dispose();
    cached = createFrameMicroSurfaceTexture();
    cacheRevision = PROC_REVISION;
  }
  return cached;
}
